import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { XIcon } from "@phosphor-icons/react";
import { useArtifactPanelActions } from "../../../FileViewer/ArtifactPanelActions";
import ArtifactPaneHeader from "../../../FileViewer/ArtifactPaneHeader";
import {
  ArtifactSourcesButton,
  artifactOriginLabel,
  useArtifactCitations,
} from "../../../FileViewer/ArtifactSources";
import type { ArtifactVersion } from "../../Artifacts/useArtifacts";
import { useInvalidateArtifactVersions } from "../../../FileViewer/artifactVersions";
import { useLocation } from "react-router-dom";
import ArtifactVersionHistoryPanel from "../../../FileViewer/ArtifactVersionHistoryPanel";
import { useArtifactHistory } from "../../../FileViewer/artifactHistory";
import DocumentCommentsPanel, {
  DocumentCommentMarginCard,
} from "../../../FileViewer/DocumentCommentsPanel";
import { useDocumentCommentsHost } from "../../../FileViewer/useDocumentCommentsHost";
import { EditorCommentsContext } from "../../../FpEditor/services/documentCommentsBridge";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { useViewerModeAction } from "../../ViewerModeToggle";
import { Tooltip } from "@mui/material";
import {
  resetFpAppV4Data,
  updateFpAppV4Data,
} from "../../../../redux/fpAppV4Slice";
import FpEditor from "../../../FpEditor";
import MenuBar from "../../../FpAppV4/MenuBar";
import WorkspaceControls from "../../../FpAppV4/WorkspaceControls";
import { useDocumentExport } from "../../../FpAppV4/useDocumentExport";
import { ExportIcon } from "@phosphor-icons/react";
import { PlainDocumentContext } from "../../../FpEditor/services/editorSurface";
import useEditorContent from "../../../FpEditor/services/useEditorContent";
import useWebsocketRoom from "../../../FpAppV4/hooks/useWebsocketRoom";
import MovingCubes from "../../../Animations/MovingCubes";

/** The document editor beside the chat (#11548 round 14). Named
 * ChatEditorPanel because chat already has an unrelated right-side
 * "document panel" (the viewer pane governed by documentPanelPolicy.ts) —
 * this one hosts the report/document EDITOR.
 *
 * When the model opens a document via `create_editor_document`, this panel
 * hosts the SAME editor the Artifacts surface uses for that workspace —
 * created next to the conversation so the user can edit while they chat.
 * Full chrome (TOC, sharing, scheduling) lives on the Artifacts surface;
 * the header links there.
 *
 * The editor stack keys off the global fpAppV4 workspace id, so the panel
 * owns it while open and hands it back to the conversation on close.
 * Accepted trade-off: chat-side citation lookups prefer that slice over the
 * /chat/:id path fallback (getSourceRefs.ts, useQueryInfo.ts), so while the
 * panel is open a citation backfill resolves against the document workspace
 * and falls back to the URL-derived conversation id. */
const ChatEditorPanel = ({
  workspaceId,
  title,
  conversationId,
  onClose,
  artifactId,
  hosted,
  departing,
}: {
  workspaceId: string;
  title: string | null;
  conversationId: string | null;
  onClose: () => void;
  // The backing artifact (r51): with it the header offers the same
  // Version history menu every other pane has.
  artifactId?: string | null;
  // Hosted inside the Layout split pane (r51): the pane owns width and
  // resizing, so the panel renders frameless.
  hosted?: boolean;
  // Sliding out of the overlay while the split pane's copy of this document
  // has already mounted (#12481): the slice is that copy's now, so this
  // one's unmount must not hand it back.
  departing?: boolean;
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const invalidateVersions = useInvalidateArtifactVersions();
  const [isLeftBarOpen, setIsLeftBarOpen] = useState(false);
  const [showTableOfContents, setShowTableOfContents] = useState(false);
  // Bumped when the model writes into this document (#11548 r23): the
  // workspace query refetches and the editor remounts on the new content
  // (the editor sets content once at mount by design).
  const [contentEpoch, setContentEpoch] = useState(0);
  // A read-only look at a past version (r51): the live editor stays
  // untouched until an explicit restore.
  const [viewingVersion, setViewingVersion] = useState<{
    versionNumber: number;
    html: string;
  } | null>(null);
  useEffect(() => {
    const onRefresh = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        workspaceId?: unknown;
      } | null;
      if (detail?.workspaceId !== workspaceId) return;
      // A chat write is a new version (#12481): the history column
      // re-reads the list with the document.
      invalidateVersions(artifactId);
      void queryClient
        .invalidateQueries({ queryKey: ["workspaceData", workspaceId] })
        .then(() => setContentEpoch((epoch) => epoch + 1));
    };
    window.addEventListener("editorDocumentRefresh", onRefresh);
    return () => window.removeEventListener("editorDocumentRefresh", onRefresh);
  }, [queryClient, workspaceId, artifactId, invalidateVersions]);

  const {
    data: editorData,
    isLoading,
    isPending,
  } = useEditorContent(workspaceId);

  useWebsocketRoom("workspace", workspaceId || null, "default");

  // Read at unmount through a ref: `departing` turns true in the render
  // that starts the slide, and an effect dependency would run this cleanup
  // (and the reset) right then.
  const departingRef = useRef(departing ?? false);
  departingRef.current = departing ?? false;
  useEffect(() => {
    dispatch(updateFpAppV4Data({ workspaceId }));
    return () => {
      if (departingRef.current) return;
      // Hand the slice back to the conversation (the chat wrote it on load
      // and never reads it, but a stale document id must not linger).
      dispatch(resetFpAppV4Data());
      if (conversationId) {
        dispatch(
          updateFpAppV4Data({ workspaceId: conversationId, type: "chat" })
        );
      }
    };
  }, [dispatch, workspaceId, conversationId]);

  // Version history presentation (#11819): the other header actions stand
  // down while it is open, and it renders as a column beside the editor.
  const liveHistory = useArtifactHistory(artifactId);
  // Comments on the document (#13194): a column beside the editor (a sheet
  // at phone), exclusive with version history. Unlike history, it opens in
  // place (the pane never moves to the overlay for it), and the other
  // header actions keep working while it is open.
  const liveComments = useDocumentCommentsHost({ artifactId, workspaceId });
  // A departing copy shows its side column as it last showed it live
  // (#13194). Closing version history opened from side-by-side hands the
  // document back to the split pane, and the close and the hand-off land in
  // one commit: read live, the copy sliding out of the overlay would drop
  // its column before it slid. Comments never hand off, but a document the
  // overlay hands back for another reason keeps its comments as they were
  // the same way. The live hooks keep running, since this copy still counts as a
  // host until the drawer's exit unmounts it, and the snapshot dies with it.
  const lastLive = useRef({ history: liveHistory, comments: liveComments });
  if (!departing) {
    lastLive.current = { history: liveHistory, comments: liveComments };
  }
  const { history, comments } = lastLive.current;
  const phone = useIsPhoneViewport();
  const { pathname } = useLocation();
  // The document's citations (#11975): the count follows every chat write
  // (contentEpoch bumps when one lands).
  const citations = useArtifactCitations(artifactId, contentEpoch);
  const documentExport = useDocumentExport();
  const { actions: artifactActions, dialogs: artifactDialogs } =
    useArtifactPanelActions({
      artifactId: artifactId ?? null,
      title: title ?? "Document",
      // The document's one Export (#13261 R5): a button beside Share and
      // "Export…" in the title's menu, both opening the format menu.
      file: {
        key: "export",
        label: "Export",
        menuLabel: "Export…",
        icon: ExportIcon,
        disabled: documentExport.disabled,
        onSelect: documentExport.open,
      },
    });
  const viewerModeAction = useViewerModeAction(true);
  // The history column's way of showing a version (#12481): a past
  // snapshot read-only, or the live document for null.
  const showVersion = (version: ArtifactVersion | null) => {
    if (version === null) {
      setViewingVersion(null);
    } else if (version.content) {
      setViewingVersion({
        versionNumber: version.version_number,
        html: version.content,
      });
    }
  };

  return (
    <div
      className={
        hosted
          ? // The Layout pane owns the frame: width, border, resizing.
            "relative flex h-full w-full flex-col bg-white"
          : "relative flex h-full w-[46%] min-w-[420px] max-w-[760px] flex-none flex-col border-l border-[#e7e5e4] bg-white"
      }
      data-testid="chat-editor-panel"
      // Layout hook, NOT a test id: the page composer's group-has clamp keys
      // off this attribute (review r20 minor — a testid is renameable by
      // convention, so production layout must not depend on one).
      data-chat-editor-panel=""
    >
      <ArtifactPaneHeader
        title={title || "Document"}
        badge={{ ext: "DOC", kind: "Document" }}
        origin={artifactOriginLabel(citations, pathname)}
        updatedAt={citations.updatedAt}
        comments={
          artifactId
            ? {
                openCount: comments.comments.openCount,
                open: comments.open,
                onToggle: comments.toggle,
              }
            : undefined
        }
        version={
          artifactId
            ? {
                viewing: viewingVersion?.versionNumber ?? null,
                onView: showVersion,
                historyOpen: history.open,
                onToggleHistory: history.toggle,
              }
            : undefined
        }
        sources={
          <ArtifactSourcesButton
            sourceMap={citations.sourceMap}
            citationScope={citations.citationScope}
          />
        }
        onClose={onClose}
        leading={
          // A document with no artifact behind it has no artifact Share:
          // the workspace's own Share stands in, in the pane itself. Export
          // is the cluster's either way.
          !history.open && !artifactId ? (
            <div className="flex flex-none items-center">
              <WorkspaceControls
                hideMakeCopy
                hideDelete
                hideModeToggle
                hideExport
              />
            </div>
          ) : undefined
        }
        actions={[
          ...artifactActions,
          ...(!history.open && viewerModeAction ? [viewerModeAction] : []),
        ]}
      >
        {artifactDialogs}
        {documentExport.ui}
      </ArtifactPaneHeader>
      <div className="flex min-h-0 flex-1">
        <div className="relative min-h-0 min-w-0 flex-1 overflow-auto">
          {isLoading || isPending ? (
            <div className="flex h-full w-full flex-col items-center justify-center">
              <MovingCubes numberOfCubes={4} />
              <div className="mt-4 text-[13px] text-[#6E7480]">
                Loading document…
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col">
              {/* The formatting navbar the Artifacts surface gets from
                WorkspaceHeader — mounted inside the plain-document context
                so report-only controls stay hidden here (#11548 r19). */}
              {/* No formatting toolbar over a snapshot (#11835): a past
                  version is read-only. */}
              {!viewingVersion && (
                <PlainDocumentContext.Provider value={true}>
                  {/* A floating pill over the document, not a full-width
                      bar with a rule (#12481, Gerry): the pane is one card
                      and its chrome sits on it. Clear of the header's
                      hairline by the gap the table's grid keeps under it. */}
                  <div className="flex flex-none justify-center px-4 pb-1 pt-2.5">
                    {/* Padded on every side so a button's highlight sits
                        inside the pill, not against its border; the menu
                        bar inside is the one scroller (Gerry, 2026-10-13). */}
                    <div className="max-w-full overflow-hidden rounded-[10px] border border-[var(--fp-border)] bg-[var(--fp-surface)] p-1 [box-shadow:var(--fp-lift)]">
                      <MenuBar />
                    </div>
                  </div>
                </PlainDocumentContext.Provider>
              )}
              <div className="min-h-0 flex-1 overflow-auto">
                {viewingVersion ? (
                  // A past version renders in the same editor as the live
                  // document, read-only (#11835): a raw HTML render had
                  // different page padding and type sizes, so the document
                  // visibly changed shape when a version was picked.
                  <FpEditor
                    key={`version-${viewingVersion.versionNumber}`}
                    plainDocument
                    readOnly
                    flatGround
                    editorContent={viewingVersion.html}
                    isLeftBarOpen={false}
                    setIsLeftBarOpen={() => {}}
                    hideLeftSidebar={true}
                  />
                ) : (
                  <EditorCommentsContext.Provider
                    value={artifactId ? comments.bridge : null}
                  >
                    <FpEditor
                      key={contentEpoch}
                      plainDocument
                      flatGround
                      editorContent={(editorData as unknown as string) || null}
                      isLeftBarOpen={isLeftBarOpen}
                      setIsLeftBarOpen={setIsLeftBarOpen}
                      hideLeftSidebar={true}
                      showTableOfContents={showTableOfContents}
                      setShowTableOfContents={setShowTableOfContents}
                    />
                  </EditorCommentsContext.Provider>
                )}
              </div>
            </div>
          )}
        </div>
        {comments.margin && artifactId && !viewingVersion && (
          <DocumentCommentMarginCard
            placement={comments.margin}
            artifactId={artifactId}
            comments={comments.comments}
            inDocument={comments.inDocument}
            onDismiss={comments.dismissMargin}
          />
        )}
        {comments.open && artifactId && (
          <DocumentCommentsPanel
            artifactId={artifactId}
            comments={comments.comments}
            inDocument={comments.inDocument}
            activeThreadId={comments.activeThreadId}
            onActivate={comments.activate}
            draft={comments.draft}
            onCancelDraft={comments.cancelDraft}
            onSubmitDraft={comments.submitDraft}
            presentation={phone ? "sheet" : "column"}
            onClose={comments.close}
          />
        )}
        {history.open && artifactId && (
          <ArtifactVersionHistoryPanel
            artifactId={artifactId}
            viewing={viewingVersion?.versionNumber ?? null}
            onView={showVersion}
            onRestored={() => {
              setViewingVersion(null);
              void queryClient
                .invalidateQueries({
                  queryKey: ["workspaceData", workspaceId],
                })
                .then(() => setContentEpoch((epoch) => epoch + 1));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ChatEditorPanel;
